import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setToken } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * AuthCallback - Handles Google OAuth redirect
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const hasProcessed = useRef(false);

    useEffect(() => {
        // Prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            try {
                // Extract session_id from URL fragment
                const hash = window.location.hash;
                const params = new URLSearchParams(hash.substring(1));
                const sessionId = params.get('session_id');

                if (!sessionId) {
                    console.error('No session_id found in URL');
                    navigate('/login', { replace: true });
                    return;
                }

                // Exchange session_id for user data and token
                const response = await api.post('/auth/session', {
                    session_id: sessionId
                });

                const { user, access_token, needs_onboarding } = response.data;

                // Store token and user using centralized session management
                if (access_token) {
                    setSession(access_token, user);
                } else {
                    localStorage.setItem('user', JSON.stringify(user));
                }

                // Clear the hash from URL
                window.history.replaceState(null, '', window.location.pathname);

                // Redirect based on onboarding status
                if (needs_onboarding || !user.role) {
                    navigate('/onboarding', { replace: true, state: { user } });
                } else {
                    // Redirect to role-appropriate dashboard
                    const dashboardRoutes = {
                        admin: '/admin',
                        agent: '/agent',
                        seller: '/seller'
                    };
                    navigate(dashboardRoutes[user.role] || '/dashboard', { 
                        replace: true, 
                        state: { user } 
                    });
                }
            } catch (error) {
                console.error('Auth callback error:', error);
                localStorage.removeItem('user');
                navigate('/login', { 
                    replace: true, 
                    state: { error: 'Échec de l\'authentification. Veuillez réessayer.' } 
                });
            }
        };

        processAuth();
    }, [navigate, setSession]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
                <div className="spinner w-12 h-12 mx-auto mb-4"></div>
                <p className="text-muted-foreground">Connexion en cours...</p>
            </div>
        </div>
    );
}
